import * as React from "react";

import type { OrgModelConfigSummary } from "@/shared/domain/orgs";
import {
  useBackfillOrgModelConfigs,
  useBedrockCatalog,
} from "@/shared/domain/orgs/hooks";
import { RobotoButton } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";

import { AddModelDialog } from "./AddModelDialog";
import styles from "./AiModels.module.css";
import { OrgModelConfigTable } from "./OrgModelConfigTable";
import { OrgModelsDialog } from "./OrgModelsDialog";
import { RemoveModelDialog } from "./RemoveModelDialog";

/**
 * Roboto-admin view of every org's chat model configuration: the
 * server-filtered org table, "Add model" and "Remove model" for the
 * targeted orgs, "Edit models" for one org, and a backfill for orgs still
 * missing a configuration row.
 */
export function OrgModelConfigs() {
  const catalogQuery = useBedrockCatalog();
  const backfill = useBackfillOrgModelConfigs();
  const [dialog, setDialog] = React.useState<{
    kind: "add" | "remove";
    orgIds: string[];
    clearTarget: () => void;
  } | null>(null);
  const [editing, setEditing] = React.useState<OrgModelConfigSummary | null>(
    null,
  );

  const closeDialog = (open: boolean) => {
    if (!open) {
      setDialog(null);
    }
  };

  const chatModels = React.useMemo(
    () => catalogQuery.data ?? [],
    [catalogQuery.data],
  );

  return (
    <div className={styles.container}>
      <div className={styles.backfill}>
        {backfill.isSuccess && (
          <span className={styles.note} role="status">
            Created {backfill.data} missing{" "}
            {backfill.data === 1 ? "row" : "rows"}.
          </span>
        )}
        {backfill.isError && (
          <span className={styles.error} role="alert">
            {userFacingErrorMessage(backfill.error)}
          </span>
        )}
        <RobotoButton
          variant="secondary"
          size="sm"
          loading={backfill.isPending}
          onClick={() => backfill.mutate()}
        >
          Create missing rows
        </RobotoButton>
      </div>

      <OrgModelConfigTable
        chatModels={chatModels}
        renderActions={(orgIds, clearTarget) => (
          <>
            <RobotoButton
              size="sm"
              disabled={orgIds.length === 0}
              onClick={() => setDialog({ kind: "add", orgIds, clearTarget })}
            >
              Add model
            </RobotoButton>
            <RobotoButton
              variant="secondary"
              size="sm"
              disabled={orgIds.length === 0}
              onClick={() => setDialog({ kind: "remove", orgIds, clearTarget })}
            >
              Remove model
            </RobotoButton>
          </>
        )}
        onEditOrg={setEditing}
      />

      <AddModelDialog
        open={dialog?.kind === "add"}
        onOpenChange={closeDialog}
        orgIds={dialog?.orgIds ?? []}
        chatModels={chatModels}
        onApplied={() => dialog?.clearTarget()}
      />
      <RemoveModelDialog
        open={dialog?.kind === "remove"}
        onOpenChange={closeDialog}
        orgIds={dialog?.orgIds ?? []}
        chatModels={chatModels}
        onApplied={() => dialog?.clearTarget()}
      />
      <OrgModelsDialog
        org={editing}
        onClose={() => setEditing(null)}
        chatModels={chatModels}
      />
    </div>
  );
}
